import type { RapidDataType } from "@/store/slices/rapidDataSlice";
import { projectSelector } from "@rflocal/shared";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { DelayedTooltip } from "@scripts/react/components/DelayedTooltip";
import { Chip, type ChipColor } from "@scripts/react/components/Chip";
import { CIEvaluationResult } from "@functions/continuousImprovement/continuousImprovementTypes";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useRef, useState } from "react";
import { DropdownMenu } from "../DropdownMenu";
import { DeleteModelModal } from "./DeleteModelModal";

interface BadgeConfig {
    label: string;
    color: ChipColor;
    icon: string;
}

function getStatusBadge(model: RapidDataType, userId: string | undefined): BadgeConfig | null {
    // Priority: new model ready > training
    if (model.modelChanges?.length && userId) {
        const latestChange = model.modelChanges[model.modelChanges.length - 1];
        if (!latestChange.seenBy.includes(userId)) {
            return { label: "New Version Ready", color: "purple", icon: "fas fa-circle-small" };
        }
    }

    const result = model.lastCIEvaluation?.result;
    if (result === CIEvaluationResult.TRAINING_IN_PROGRESS) {
        return { label: "Training...", color: "gray", icon: "far fa-spinner fa-spin" };
    }

    return null;
}

interface ModelCardProps {
    model: RapidDataType;
    onModelClick: (model: RapidDataType) => void;
    formatTimestamp: (date: Date | undefined) => string;
    onModelDelete: (model: RapidDataType) => void;
}

export const ModelCard = ({
    model,
    onModelClick,
    formatTimestamp,
    onModelDelete
}: ModelCardProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const observerRef = useRef<HTMLDivElement>(null);

    // Lazy load images using Intersection Observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin: "100px" }
        );

        if (observerRef.current) {
            observer.observe(observerRef.current);
        }

        return () => observer.disconnect();
    }, []);

    // Use the first dataset for display purposes
    const primaryDataset = model.datasets?.[0];

    const getBackgroundImage = () => {
        if (!primaryDataset?.icon) {
            return undefined;
        }

        const { icon, iconOwner, iconHasAnnotation, annotation } = primaryDataset;
        const baseUrl = `https://${projectSelector.cdnHost()}/${iconOwner}/${icon}`;

        if (iconHasAnnotation && annotation) {
            return `url(${baseUrl}/annotation-${annotation}.png), url(${baseUrl}/original.jpg)`;
        }

        return `url(${baseUrl}/original.jpg)`;
    };

    const { user } = useAuth();
    const badge = getStatusBadge(model, user?.uid);

    return (
        <div
            ref={observerRef}
            onClick={() => onModelClick(model)}
            className={twClassNames(
                "group flex cursor-pointer flex-col bg-white p-0.5 transition-all"
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Thumbnail */}
            <div
                className={twClassNames(
                    "relative flex overflow-hidden rounded-lg bg-white p-0.5 ring-1 transition duration-200",
                    isHovered ? "shadow-md ring-2 ring-brand" : "ring-gray-300"
                )}
                style={{ aspectRatio: 16 / 9 }}
            >
                {isVisible && primaryDataset?.icon ? (
                    <div
                        className="h-full w-full rounded-md bg-cover bg-center bg-no-repeat"
                        style={{ backgroundImage: getBackgroundImage() }}
                    />
                ) : (
                    <div
                        className={twClassNames(
                            "flex h-full w-full items-center justify-center rounded-md transition duration-200",
                            isHovered ? "bg-tint text-purboflow-500" : "text-border bg-gray-100"
                        )}
                    >
                        <i className="far fa-image text-3xl" />
                    </div>
                )}

                {badge && (
                    <div className="absolute bottom-2 right-2 z-10">
                        <Chip
                            color={badge.color}
                            size="small"
                            className="shadow-md ring-[1px] ring-black/10"
                        >
                            <i className={badge.icon} />
                            {badge.label}
                        </Chip>
                    </div>
                )}
            </div>

            {/* Project Info */}
            <div className="flex items-start justify-between gap-2 py-2">
                {/* Content */}
                <div className="flex flex-col gap-0.5 truncate">
                    {/* Model name */}
                    <DelayedTooltip
                        content={model.workflowName || "Untitled Model"}
                        delay={[500, 0]}
                        placement="top"
                        readOnly
                        className={twClassNames(
                            "font-semibold",
                            isHovered ? "text-brand" : "text-primary"
                        )}
                    />

                    {/* Creation timestamp */}
                    {primaryDataset?.createdAt && (
                        <p
                            className={twClassNames(
                                "text-xs",
                                isHovered ? "text-brand" : "text-tertiary"
                            )}
                        >
                            {formatTimestamp(
                                typeof primaryDataset.createdAt === "string"
                                    ? new Date(primaryDataset.createdAt)
                                    : primaryDataset.createdAt
                            )}
                        </p>
                    )}
                </div>

                <DropdownMenu
                    items={[{ id: "delete", label: "Delete", icon: "fas fa-trash-can" }]}
                    selectedItem={{ id: "delete", label: "Delete", icon: "fas fa-trash-can" }}
                    onSelectItem={() => setIsDeleteModalOpen(true)}
                    getItemLabel={(item) => item.label}
                    getItemKey={(item) => item.id}
                    getLeftContent={(item) => <i className={`${item.icon} fa-fw`} />}
                    getItemClassName={() => "danger"}
                    buttonClassName="btn2 ghost xxsmall square"
                    customMenuButton={<i className="fas fa-ellipsis-v" />}
                    menuItemsClassName="right-0 top-full mt-1 w-32"
                />
            </div>

            {isDeleteModalOpen && (
                <DeleteModelModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    modelToDelete={model}
                    onConfirm={() => onModelDelete(model)}
                />
            )}
        </div>
    );
};
